import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Paper,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { 
  classTypeAPI, 
  classLevelAPI, 
  amenityTypeAPI, 
  dropdownManagementAPI 
} from '../../services/api';
import { ClassType, ClassLevel, AmenityType, DropdownUsageInfo } from '../../types';

interface DeleteDialogState {
  open: boolean;
  type: 'classType' | 'classLevel' | 'amenityType';
  item: ClassType | ClassLevel | AmenityType | null;
  usageInfo: DropdownUsageInfo | null;
}

interface CreateDialogState {
  open: boolean;
  type: 'classType' | 'classLevel' | 'amenityType';
  name: string;
  description: string;
  color?: string;
}

const DropdownManagement: React.FC = () => {
  // Default pastel colors for class types
  const defaultPastelColors = [
    '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF',
    '#E0BBE4', '#FFD1DC', '#C7CEEA', '#B5EAD7', '#FFDAC1'
  ];

  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [classLevels, setClassLevels] = useState<ClassLevel[]>([]);
  const [amenityTypes, setAmenityTypes] = useState<AmenityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingColor, setUpdatingColor] = useState<string | null>(null);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [editingTypeName, setEditingTypeName] = useState<string>('');
  const [classTypesExpanded, setClassTypesExpanded] = useState(false);
  
  // Selected items for deletion
  const [selectedClassType, setSelectedClassType] = useState<string>('');
  const [selectedClassLevel, setSelectedClassLevel] = useState<string>('');
  const [selectedAmenityType, setSelectedAmenityType] = useState<string>('');
  
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    type: 'classType',
    item: null,
    usageInfo: null
  });
  const [createDialog, setCreateDialog] = useState<CreateDialogState>({
    open: false,
    type: 'classType',
    name: '',
    description: ''
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classTypesRes, classLevelsRes, amenityTypesRes] = await Promise.all([
        classTypeAPI.getAll(),
        classLevelAPI.getAll(),
        amenityTypeAPI.getAll()
      ]);

      setClassTypes(classTypesRes.data);
      setClassLevels(classLevelsRes.data);
      setAmenityTypes(amenityTypesRes.data);
    } catch (error: any) {
      console.error('Error fetching dropdown data:', error);
      setError('Failed to load dropdown data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (
    type: 'classType' | 'classLevel' | 'amenityType',
    itemId: string
  ) => {
    if (!itemId) return;
    
    let item: ClassType | ClassLevel | AmenityType | undefined;
    
    switch (type) {
      case 'classType':
        item = classTypes.find(ct => ct.id === itemId);
        break;
      case 'classLevel':
        item = classLevels.find(cl => cl.id === itemId);
        break;
      case 'amenityType':
        item = amenityTypes.find(at => at.id === itemId);
        break;
    }
    
    if (!item) return;
    
    try {
      let usageInfo: DropdownUsageInfo;
      
      switch (type) {
        case 'classType':
          usageInfo = (await dropdownManagementAPI.getClassTypeUsage(item.id)).data;
          break;
        case 'classLevel':
          usageInfo = (await dropdownManagementAPI.getClassLevelUsage(item.id)).data;
          break;
        case 'amenityType':
          usageInfo = (await dropdownManagementAPI.getAmenityTypeUsage(item.id)).data;
          break;
        default:
          return;
      }

      setDeleteDialog({
        open: true,
        type,
        item,
        usageInfo
      });
    } catch (error: any) {
      console.error('Error checking usage:', error);
      setError('Failed to check usage information');
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.item) return;

    try {
      switch (deleteDialog.type) {
        case 'classType':
          await dropdownManagementAPI.deleteClassType(deleteDialog.item.id);
          setSuccessMessage('Class type deleted successfully');
          setSelectedClassType('');
          break;
        case 'classLevel':
          await dropdownManagementAPI.deleteClassLevel(deleteDialog.item.id);
          setSuccessMessage('Class level deleted successfully');
          setSelectedClassLevel('');
          break;
        case 'amenityType':
          await dropdownManagementAPI.deleteAmenityType(deleteDialog.item.id);
          setSuccessMessage('Amenity type deleted successfully');
          setSelectedAmenityType('');
          break;
      }

      // Refresh data
      await fetchData();
      setDeleteDialog({ open: false, type: 'classType', item: null, usageInfo: null });
    } catch (error: any) {
      console.error('Error deleting item:', error);
      
      // Check if it's a usage conflict error
      if (error.response?.status === 409) {
        const usedIn = error.response.data.usedIn || [];
        setDeleteDialog(prev => ({
          ...prev,
          usageInfo: { isUsed: true, usedIn }
        }));
        setError('Cannot delete: item is currently being used');
      } else {
        setError(error.response?.data?.message || 'Failed to delete item');
      }
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, type: 'classType', item: null, usageInfo: null });
  };

  const handleColorUpdate = async (classTypeId: string, newColor: string) => {
    try {
      setUpdatingColor(classTypeId);
      await classTypeAPI.update(classTypeId, { color: newColor });
      
      // Update local state
      setClassTypes(prev => prev.map(ct => 
        ct.id === classTypeId ? { ...ct, color: newColor } : ct
      ));
      
      setSuccessMessage('Color updated successfully');
    } catch (error: any) {
      console.error('Error updating color:', error);
      setError(error.response?.data?.message || 'Failed to update color');
    } finally {
      setUpdatingColor(null);
    }
  };

  const handleStartEdit = (classType: ClassType) => {
    setEditingTypeId(classType.id);
    setEditingTypeName(classType.name);
  };

  const handleCancelEdit = () => {
    setEditingTypeId(null);
    setEditingTypeName('');
  };

  const handleSaveEdit = async (classTypeId: string) => {
    if (!editingTypeName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    try {
      await classTypeAPI.update(classTypeId, { name: editingTypeName.trim() });
      
      // Update local state
      setClassTypes(prev => prev.map(ct => 
        ct.id === classTypeId ? { ...ct, name: editingTypeName.trim() } : ct
      ));
      
      setSuccessMessage('Class type name updated successfully');
      setEditingTypeId(null);
      setEditingTypeName('');
    } catch (error: any) {
      console.error('Error updating name:', error);
      setError(error.response?.data?.message || 'Failed to update name');
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    
    const newClassTypes = [...classTypes];
    [newClassTypes[index - 1], newClassTypes[index]] = [newClassTypes[index], newClassTypes[index - 1]];
    setClassTypes(newClassTypes);
    
    // TODO: Implement backend API to persist order
    setSuccessMessage('Order updated');
  };

  const handleMoveDown = (index: number) => {
    if (index === classTypes.length - 1) return;
    
    const newClassTypes = [...classTypes];
    [newClassTypes[index], newClassTypes[index + 1]] = [newClassTypes[index + 1], newClassTypes[index]];
    setClassTypes(newClassTypes);
    
    // TODO: Implement backend API to persist order
    setSuccessMessage('Order updated');
  };

  const handleCreateClick = (type: 'classType' | 'classLevel' | 'amenityType') => {
    setCreateDialog({
      open: true,
      type,
      name: '',
      description: '',
      color: type === 'classType' ? defaultPastelColors[classTypes.length % defaultPastelColors.length] : undefined
    });
  };

  const closeCreateDialog = () => {
    setCreateDialog({
      open: false,
      type: 'classType',
      name: '',
      description: '',
      color: undefined
    });
  };

  const confirmCreate = async () => {
    if (!createDialog.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      const data: any = {
        name: createDialog.name.trim(),
        description: createDialog.description.trim() || undefined
      };

      // Add color for class types
      if (createDialog.type === 'classType') {
        data.color = createDialog.color || defaultPastelColors[0];
      }

      switch (createDialog.type) {
        case 'classType':
          await classTypeAPI.create(data);
          setSuccessMessage('Class type created successfully');
          break;
        case 'classLevel':
          await classLevelAPI.create(data);
          setSuccessMessage('Class level created successfully');
          break;
        case 'amenityType':
          await amenityTypeAPI.create(data);
          setSuccessMessage('Amenity type created successfully');
          break;
      }

      // Refresh data
      await fetchData();
      closeCreateDialog();
    } catch (error: any) {
      console.error('Error creating item:', error);
      setError(error.response?.data?.message || 'Failed to create item');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Class Types - Accordion */}
        <Grid size={{ xs: 12 }}>
          <Accordion expanded={classTypesExpanded} onChange={() => setClassTypesExpanded(!classTypesExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Class Types ({classTypes.length})
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateClick('classType');
                  }}
                >
                  Add Class Type
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {classTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                  No class types found
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {classTypes.map((classType, index) => (
                    <Box
                      key={classType.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {/* Reorder buttons */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveUp(index)}
                                disabled={index === 0}
                                sx={{ p: 0.5 }}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveDown(index)}
                                disabled={index === classTypes.length - 1}
                                sx={{ p: 0.5 }}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        
                        {/* Color picker */}
                        <Box sx={{ position: 'relative' }}>
                          <input
                            type="color"
                            value={classType.color || '#FFB3BA'}
                            onChange={(e) => handleColorUpdate(classType.id, e.target.value)}
                            disabled={updatingColor === classType.id}
                            style={{
                              width: '40px',
                              height: '40px',
                              border: '2px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            title="Click to change color"
                          />
                        </Box>
                        
                        {/* Class type info with inline editing */}
                        <Box sx={{ flex: 1 }}>
                          {editingTypeId === classType.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                value={editingTypeName}
                                onChange={(e) => setEditingTypeName(e.target.value)}
                                size="small"
                                autoFocus
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEdit(classType.id);
                                  }
                                }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSaveEdit(classType.id)}
                              >
                                <CheckIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {classType.name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleStartEdit(classType)}
                                sx={{ ml: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                          {classType.description && (
                            <Typography variant="body2" color="text.secondary">
                              {classType.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Delete button */}
                      <Tooltip title="Delete class type">
                        <IconButton
                          onClick={() => handleDeleteClick('classType', classType.id)}
                          size="small"
                          sx={{
                            color: 'rgba(0, 0, 0, 0.26)',
                            '&:hover': {
                              color: 'error.main',
                              bgcolor: 'error.lighter'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Class Levels */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Class Levels ({classLevels.length})
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleCreateClick('classLevel')}
                sx={{ p: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Select to Delete</InputLabel>
                <Select
                  value={selectedClassLevel}
                  label="Select to Delete"
                  onChange={(e) => setSelectedClassLevel(e.target.value)}
                >
                  {classLevels.map((classLevel) => (
                    <MenuItem key={classLevel.id} value={classLevel.id}>
                      {classLevel.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick('classLevel', selectedClassLevel)}
                disabled={!selectedClassLevel}
                fullWidth
              >
                Delete Selected
              </Button>
              {classLevels.length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center">
                  No levels found
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Amenity Types */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Amenity Types ({amenityTypes.length})
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleCreateClick('amenityType')}
                sx={{ p: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Select to Delete</InputLabel>
                <Select
                  value={selectedAmenityType}
                  label="Select to Delete"
                  onChange={(e) => setSelectedAmenityType(e.target.value)}
                >
                  {amenityTypes.map((amenityType) => (
                    <MenuItem key={amenityType.id} value={amenityType.id}>
                      {amenityType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick('amenityType', selectedAmenityType)}
                disabled={!selectedAmenityType}
                fullWidth
              >
                Delete Selected
              </Button>
              {amenityTypes.length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center">
                  No types found
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        disableEscapeKeyDown={true}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {deleteDialog.usageInfo?.isUsed && <WarningIcon color="warning" />}
            Delete {deleteDialog.type === 'classType' ? 'Class Type' : 
                   deleteDialog.type === 'classLevel' ? 'Class Level' : 'Amenity Type'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteDialog.usageInfo?.isUsed ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This {deleteDialog.type === 'classType' ? 'class type' : 
                      deleteDialog.type === 'classLevel' ? 'class level' : 'amenity type'} 
                is currently being used and cannot be deleted.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{deleteDialog.item?.name}</strong> is being used in the following:
              </Typography>
              <Stack spacing={1}>
                {deleteDialog.usageInfo.usedIn.map((usage) => (
                  <Box key={`${usage.type}-${usage.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={usage.type === 'class' ? 'Class' : 'Amenity'} 
                      size="small" 
                      color={usage.type === 'class' ? 'primary' : 'secondary'}
                    />
                    <Typography variant="body2">{usage.name}</Typography>
                  </Box>
                ))}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                To delete this {deleteDialog.type === 'classType' ? 'class type' : 
                              deleteDialog.type === 'classLevel' ? 'class level' : 'amenity type'}, 
                please first update or remove all {deleteDialog.usageInfo.usedIn.length > 1 ? 'items' : 'item'} listed above.
              </Typography>
            </>
          ) : (
            <>
              <DialogContentText>
                Are you sure you want to delete <strong>{deleteDialog.item?.name}</strong>?
              </DialogContentText>
              <Alert severity="info" sx={{ mt: 2 }}>
                This action cannot be undone. However, this {deleteDialog.type === 'classType' ? 'class type' : 
                                                             deleteDialog.type === 'classLevel' ? 'class level' : 'amenity type'} 
                is not currently being used, so it's safe to delete.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>
            {deleteDialog.usageInfo?.isUsed ? 'Close' : 'Cancel'}
          </Button>
          {!deleteDialog.usageInfo?.isUsed && (
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialog.open}
        disableEscapeKeyDown={true}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New {createDialog.type === 'classType' ? 'Class Type' : 
                   createDialog.type === 'classLevel' ? 'Class Level' : 'Amenity Type'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={createDialog.name}
              onChange={(e) => setCreateDialog(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Description"
              value={createDialog.description}
              onChange={(e) => setCreateDialog(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              helperText="Optional description for this option"
            />
            {createDialog.type === 'classType' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={createDialog.color || defaultPastelColors[0]}
                    onChange={(e) => setCreateDialog(prev => ({ ...prev, color: e.target.value }))}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    This color will help identify this class type in the calendar
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button onClick={confirmCreate} variant="contained" disabled={!createDialog.name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DropdownManagement;
